import { token } from '@/infrastructure/container';
import { type SourceKind } from '@/domain/beforered';

export interface CanonicalFieldSpec {
  name: string;
  required: boolean;
  synonyms: readonly string[];
}

export type MappingSuggestion = Record<string, string | null>;

export interface IColumnMapper {
  spec(source: SourceKind): readonly CanonicalFieldSpec[];
  suggest(headers: readonly string[], source: SourceKind): MappingSuggestion;
}

export const TOKEN_IColumnMapper = token<IColumnMapper>('IColumnMapper');

const BOM_FIELDS: CanonicalFieldSpec[] = [
  {
    name: 'parent',
    required: true,
    synonyms: ['parent', 'parentsku', 'assembly', 'product', 'productnumber', 'fg', 'finishedgood'],
  },
  {
    name: 'child',
    required: true,
    synonyms: ['child', 'childsku', 'component', 'input', 'rawmaterial', 'material', 'partnumber'],
  },
  {
    name: 'quantityPerParent',
    required: true,
    synonyms: [
      'qty',
      'quantity',
      'quantityper',
      'qtyper',
      'required',
      'requiredqty',
      'consumption',
      'usage',
    ],
  },
  { name: 'uom', required: false, synonyms: ['uom', 'unit', 'unitofmeasure', 'units'] },
];

const INVENTORY_FIELDS: CanonicalFieldSpec[] = [
  {
    name: 'sku',
    required: true,
    synonyms: ['sku', 'part', 'partnumber', 'productnumber', 'itemnumber', 'item', 'product'],
  },
  {
    name: 'onHand',
    required: true,
    synonyms: [
      'onhand',
      'qtyonhand',
      'quantity',
      'qty',
      'available',
      'stock',
      'balance',
      'qtyavailable',
    ],
  },
  { name: 'asOf', required: false, synonyms: ['asof', 'date', 'snapshotdate', 'inventorydate'] },
];

const SALES_FIELDS: CanonicalFieldSpec[] = [
  {
    name: 'orderNumber',
    required: true,
    synonyms: [
      'orderno',
      'ordernumber',
      'sono',
      'so',
      'salesorder',
      'salesordernumber',
      'docnumber',
    ],
  },
  {
    name: 'customerName',
    required: false,
    synonyms: ['customer', 'customername', 'client', 'account', 'buyer'],
  },
  {
    name: 'sku',
    required: true,
    synonyms: ['sku', 'part', 'partnumber', 'productnumber', 'itemnumber', 'item', 'product'],
  },
  {
    name: 'quantity',
    required: true,
    synonyms: ['qty', 'quantity', 'qtyordered', 'quantityordered'],
  },
  {
    name: 'scheduledShipDate',
    required: true,
    synonyms: [
      'shipdate',
      'scheduledshipdate',
      'date',
      'duedate',
      'shipby',
      'requireddate',
      'datescheduled',
      'scheduled',
    ],
  },
];

const MFG_FIELDS: CanonicalFieldSpec[] = [
  {
    name: 'workOrderNumber',
    required: true,
    synonyms: [
      'wonum',
      'workorder',
      'workordernumber',
      'wo',
      'manufacturingorder',
      'mo',
      'monumber',
      'orderno',
      'ordernumber',
    ],
  },
  {
    name: 'sku',
    required: true,
    synonyms: ['sku', 'part', 'partnumber', 'productnumber', 'itemnumber', 'item', 'product'],
  },
  {
    name: 'quantity',
    required: true,
    synonyms: ['qty', 'quantity', 'qtytobuild', 'qtyplanned', 'quantitytobuild', 'build'],
  },
  {
    name: 'scheduledCompletionDate',
    required: true,
    synonyms: [
      'completiondate',
      'scheduledcompletion',
      'completion',
      'date',
      'duedate',
      'datescheduled',
      'scheduled',
      'dateneeded',
    ],
  },
];

const PURCHASE_FIELDS: CanonicalFieldSpec[] = [
  {
    name: 'orderNumber',
    required: true,
    synonyms: ['ponum', 'ponumber', 'purchaseorder', 'po', 'orderno', 'ordernumber', 'docnumber'],
  },
  {
    name: 'sku',
    required: true,
    synonyms: ['sku', 'part', 'partnumber', 'productnumber', 'itemnumber', 'item', 'product'],
  },
  {
    name: 'vendorId',
    required: false,
    synonyms: ['vendor', 'vendorid', 'supplier', 'supplierid', 'vendorname', 'suppliername'],
  },
  {
    name: 'quantityOutstanding',
    required: true,
    synonyms: ['qty', 'quantity', 'qtyoutstanding', 'qtyremaining', 'qtyopen', 'qtyordered'],
  },
  {
    name: 'expectedDeliveryDate',
    required: true,
    synonyms: [
      'eta',
      'duedate',
      'expecteddate',
      'expecteddelivery',
      'deliverydate',
      'date',
      'datescheduled',
      'scheduled',
    ],
  },
];

const PARTS_FIELDS: CanonicalFieldSpec[] = [
  {
    name: 'id',
    required: true,
    synonyms: ['sku', 'part', 'partnumber', 'productnumber', 'itemnumber', 'item', 'id'],
  },
  {
    name: 'description',
    required: false,
    synonyms: ['description', 'desc', 'name', 'partname', 'productname'],
  },
  { name: 'uom', required: false, synonyms: ['uom', 'unit', 'unitofmeasure', 'units'] },
  {
    name: 'productionLeadTimeDays',
    required: false,
    synonyms: ['productionleadtime', 'productionlead', 'mfgleadtime', 'mfglead'],
  },
  {
    name: 'purchaseLeadTimeDays',
    required: false,
    synonyms: ['purchaseleadtime', 'purchaselead', 'leadtime', 'vendorleadtime'],
  },
  {
    name: 'safetyStockUnits',
    required: false,
    synonyms: ['safetystock', 'safety', 'reorderpoint', 'minstock'],
  },
];

const FIELDS: Record<SourceKind, readonly CanonicalFieldSpec[]> = {
  BOM: BOM_FIELDS,
  INVENTORY: INVENTORY_FIELDS,
  SALES: SALES_FIELDS,
  MFG: MFG_FIELDS,
  PURCHASE: PURCHASE_FIELDS,
  PARTS: PARTS_FIELDS,
};

const normalize = (header: string): string =>
  header
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '')
    .replace(/\d+$/, '');

const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  let prev = new Array<number>(bl + 1);
  let curr = new Array<number>(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    for (let j = 1; j <= bl; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
};

export class ColumnMapper implements IColumnMapper {
  spec(source: SourceKind): readonly CanonicalFieldSpec[] {
    return FIELDS[source];
  }

  suggest(headers: readonly string[], source: SourceKind): MappingSuggestion {
    const fields = FIELDS[source];
    const normalized = headers.map((h) => ({ raw: h, norm: normalize(h) }));
    const result: MappingSuggestion = {};
    const used = new Set<string>();
    for (const f of fields) {
      const normSynonyms = f.synonyms.map((s) => s.toUpperCase().replace(/[^A-Z0-9]+/g, ''));
      let best: { raw: string; score: number } | null = null;
      for (const h of normalized) {
        if (used.has(h.raw) || h.norm.length === 0) continue;
        let score = Infinity;
        for (const s of normSynonyms) {
          if (h.norm === s) {
            score = 0;
            break;
          }
          if (s.length >= 3 && h.norm.startsWith(s)) {
            score = Math.min(score, 0.2);
            continue;
          }
          const d = levenshtein(h.norm, s);
          const ratio = d / Math.max(h.norm.length, s.length);
          if (ratio < score) score = ratio;
        }
        if (!best || score < best.score) best = { raw: h.raw, score };
      }
      if (best && best.score <= 0.4) {
        result[f.name] = best.raw;
        used.add(best.raw);
      } else {
        result[f.name] = null;
      }
    }
    return result;
  }
}
